import React, { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { addDoc, arrayUnion, collection, deleteDoc, doc, limit, onSnapshot, orderBy, query, writeBatch } from 'firebase/firestore';
import { FiAlertCircle, FiCheck, FiCornerUpLeft, FiExternalLink, FiMessageSquare, FiPaperclip, FiSend, FiTrash2, FiX } from 'react-icons/fi';
import { db } from '../../lib/firebase';
import { unitsOf, useSessionUser } from '../../lib/permissions';
import { unitConstraints } from '../../lib/unitQuery';
import { archiveDocuments, documentError, formatSize, sendFiles, storedDocuments } from '../../lib/documents';
import { BUTTON, CARD, EmptyState, Modal, Notice, PageHeader, Segmented, SIZE, SkeletonRows } from '../ui/kit';
import { useDocumentsStatus } from '../documents/AttachmentsField';
import DocumentLinks from '../documents/DocumentLinks';
import DocumentViewer from '../documents/DocumentViewer';
import { authorOf, channelsOf, dayLabel, inChannel, isUnreadFor, messageAccess, readersOf, timeLabel } from './messageModel';

// Latest messages read at once (every channel); older ones come with "Afficher plus". A
// channel shows its SHOWN latest messages, then SHOWN more at each request.
const BATCH = 400;
const SHOWN = 150;
const LINK_BUTTON = 'inline-flex cursor-pointer items-center gap-1 border-0 bg-[transparent] p-0 text-[12px] font-medium text-black/40 hover:text-ink';

const Bubble = memo(({ message, own, showAuthor, canDelete, onReply, onDelete, onOpen }) => {
  const documents = storedDocuments(message.documents);
  const readers = own ? readersOf(message) : [];
  const quote = message.replyTo || (message.isComment ? { sender: '', text: message.originalFileName || message.originalMessage } : null);
  return (
    <li className={`flex flex-col ${own ? 'items-end' : 'items-start'}`}>
      {showAuthor && !own && <span className="mb-1 px-1 text-[12.5px] font-semibold text-black/55">{authorOf(message)}</span>}
      <div
        className={`max-w-[85%] break-words rounded-2xl px-[14px] py-[10px] text-[14.5px] leading-relaxed sm:max-w-[70%] ${
          own ? 'rounded-br-md bg-brand-600 text-white' : 'rounded-bl-md bg-white text-ink shadow-[0_1px_2px_rgba(0,0,0,0.08)]'
        }`}
      >
        {quote && quote.text && (
          <div className={`mb-1.5 border-0 border-l-[3px] border-solid pl-2 text-[12.5px] ${own ? 'border-white/50 text-white/80' : 'border-brand-600/40 text-black/50'}`}>
            {quote.sender ? <span className="font-semibold">{quote.sender} : </span> : 'Re : '}
            <span className="line-clamp-2">{quote.text}</span>
          </div>
        )}
        {message.text && <p className="m-0 whitespace-pre-wrap">{message.text}</p>}
        {documents.length > 0 && (
          <div className={`mt-1.5 rounded-xl px-2 py-1.5 ${own ? 'bg-white' : 'bg-mist'}`}>
            <DocumentLinks documents={documents} onOpen={onOpen} size="sm" />
          </div>
        )}
        {message.fileURL && (
          <a href={message.fileURL} target="_blank" rel="noopener noreferrer" className={`mt-1.5 inline-flex items-center gap-1 break-all text-[13px] font-medium no-underline ${own ? 'text-white underline' : 'text-brand-600'}`}>
            <FiExternalLink size={12} aria-hidden="true" className="flex-none" />
            {message.fileName || 'Fichier'}
          </a>
        )}
      </div>
      <div className={`mt-1 flex items-center gap-[10px] px-1 ${own ? 'flex-row-reverse' : ''}`}>
        <span className="text-[11.5px] text-black/35">
          {timeLabel(message.timestamp)}
          {own && readers.length > 0 && <span title={readers.join(', ')}> · Lu par {readers.length}</span>}
        </span>
        {onReply && (
          <button type="button" onClick={() => onReply(message)} className={LINK_BUTTON} aria-label={`Répondre à ${authorOf(message)}`}>
            <FiCornerUpLeft size={12} aria-hidden="true" />
            Répondre
          </button>
        )}
        {canDelete && (
          <button type="button" onClick={() => onDelete(message)} className={`${LINK_BUTTON} hover:text-red-600`} aria-label="Supprimer le message">
            <FiTrash2 size={12} aria-hidden="true" />
            Supprimer
          </button>
        )}
      </div>
    </li>
  );
});

const MessagesPage = () => {
  const user = useSessionUser();
  const access = messageAccess(user);
  const documentsStatus = useDocumentsStatus();
  const channels = useMemo(() => channelsOf(user), [user]);
  const [channel, setChannel] = useState(channels[0] || '');
  const [max, setMax] = useState(BATCH);
  const [shown, setShown] = useState(SHOWN);
  const [state, setState] = useState({ status: 'loading', items: [] });
  const [text, setText] = useState('');
  const [pending, setPending] = useState([]);
  const [uploaded, setUploaded] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(null);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [viewing, setViewing] = useState(null);
  const list = useRef(null);
  const picker = useRef(null);
  const nearBottom = useRef(true);
  const marked = useRef(new Set());

  // Only the messages of the account's channels (Firestore rules).
  const constraints = useMemo(() => unitConstraints(user), [user]);
  useEffect(() => {
    if (!constraints) return undefined;
    return onSnapshot(
      query(collection(db, 'messages'), ...constraints, orderBy('timestamp', 'desc'), limit(max)),
      (snapshot) => setState({ status: 'ready', items: snapshot.docs.map((item) => ({ id: item.id, ...item.data() })), full: snapshot.size >= max }),
      () => setState({ status: 'error', items: [] })
    );
  }, [constraints, max]);

  // Messages of the channel, oldest first, grouped by day.
  const messages = useMemo(() => state.items.filter((message) => inChannel(message, channel)).reverse(), [state.items, channel]);
  const visible = useMemo(() => messages.slice(-shown), [messages, shown]);
  const hasOlder = messages.length > visible.length || state.full;
  const days = useMemo(() => {
    const result = [];
    visible.forEach((message, index) => {
      const label = dayLabel(message.timestamp);
      const previous = visible[index - 1];
      const last = result[result.length - 1];
      const sameDay = last && last.label === label;
      // The author's name is shown once for a series of messages within five minutes.
      const showAuthor = !sameDay || previous.userName !== message.userName || new Date(message.timestamp) - new Date(previous.timestamp) > 5 * 60 * 1000;
      const entry = { message, showAuthor };
      if (sameDay) last.entries.push(entry);
      else result.push({ label, entries: [entry] });
    });
    return result;
  }, [visible]);

  const unreadIn = useMemo(
    () => Object.fromEntries(channels.map((name) => [name, state.items.filter((message) => inChannel(message, name) && isUnreadFor(message, user.name)).length])),
    [channels, state.items, user.name]
  );

  // Messages of the open channel are marked as read by this account.
  useEffect(() => {
    const unread = messages.filter((message) => isUnreadFor(message, user.name) && !marked.current.has(message.id));
    if (!unread.length) return;
    const batch = writeBatch(db);
    unread.slice(0, 400).forEach((message) => {
      marked.current.add(message.id);
      batch.update(doc(db, 'messages', message.id), { readBy: arrayUnion(user.name) });
    });
    batch.commit().catch(() => unread.forEach((message) => marked.current.delete(message.id)));
  }, [messages, user.name]);

  // Stays at the latest message while the reader is at the bottom of the conversation; older
  // messages loaded above keep the reader where they were.
  const fromBottom = useRef(null);
  useLayoutEffect(() => {
    const node = list.current;
    if (!node) return;
    if (fromBottom.current !== null) {
      node.scrollTop = node.scrollHeight - fromBottom.current;
      fromBottom.current = null;
    } else if (nearBottom.current) node.scrollTop = node.scrollHeight;
  }, [visible.length, channel]);
  const showOlder = () => {
    const node = list.current;
    if (node) fromBottom.current = node.scrollHeight - node.scrollTop;
    if (messages.length <= visible.length) setMax((count) => count + BATCH);
    setShown((count) => count + SHOWN);
  };
  const onScroll = () => {
    const node = list.current;
    if (node) nearBottom.current = node.scrollHeight - node.scrollTop - node.clientHeight < 80;
  };

  const openChannel = (name) => {
    // Files already sent belong to the open channel's folder.
    if (uploaded.length || busy) {
      setError('Envoyez ou retirez d’abord les pièces jointes de ce salon.');
      return;
    }
    setError('');
    setReplyTo(null);
    nearBottom.current = true;
    fromBottom.current = null;
    setShown(SHOWN);
    setChannel(name);
  };

  const send = async () => {
    const body = text.trim();
    if ((!body && !pending.length && !uploaded.length) || busy) return;
    setError('');
    let documents = uploaded;
    if (pending.length) {
      setBusy(true);
      try {
        documents = documents.concat(await sendFiles(pending, { section: 'messages', unit: channel }, setSending));
        setPending([]);
      } catch (uploadError) {
        // Files already in the service's Dropbox are kept for the next attempt.
        const done = uploadError.sent || [];
        setUploaded(documents.concat(done));
        setPending((current) => current.slice(done.length));
        setError(await documentError(uploadError, 'Un fichier n’a pas pu être envoyé. Vérifiez la connexion, puis réessayez.'));
        return;
      } finally {
        setSending(null);
        setBusy(false);
      }
    }
    // The message shows at once; Firestore sends it as soon as the connection allows.
    addDoc(collection(db, 'messages'), {
      text: body,
      timestamp: new Date().toISOString(),
      sender: user.name || '',
      userName: user.name || '',
      userService: channel,
      unit: channel,
      units: unitsOf(channel),
      readBy: [],
      ...(documents.length ? { documents: documents.map(({ id, name, mimeType }) => ({ id, name, mimeType: mimeType || '' })) } : {}),
      ...(replyTo
        ? {
            replyTo: { id: replyTo.id, sender: authorOf(replyTo), text: String(replyTo.text || (storedDocuments(replyTo.documents)[0] || {}).name || replyTo.fileName || '').slice(0, 160) },
            // Read by the former page.
            parentMessageId: replyTo.id,
            originalMessage: String(replyTo.text || '').slice(0, 160),
            isComment: true,
          }
        : {}),
    }).catch(() => {
      setError('Le message n’a pas pu être envoyé : il a été remis dans la zone de saisie.');
      setText((current) => current || body);
      setUploaded((current) => (current.length ? current : documents));
    });
    setText('');
    setUploaded([]);
    setReplyTo(null);
    nearBottom.current = true;
  };

  const onKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      send();
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      if ((await archiveDocuments(storedDocuments(deleting.documents))).length) throw new Error('archive');
      await deleteDoc(doc(db, 'messages', deleting.id));
      setDeleting(null);
    } catch (removeError) {
      setError(removeError.message === 'archive' ? 'Une pièce jointe n’a pas pu être retirée. Réessayez.' : 'Le message n’a pas pu être supprimé.');
      setDeleting(null);
    } finally {
      setBusy(false);
    }
  };

  if (!channels.length) {
    return (
      <main className="mx-auto max-w-4xl px-[20px] pb-16 pt-8 sm:px-8 lg:px-10 lg:pt-10">
        <PageHeader eyebrow="Équipe" title="Messages" />
        <div className={`${CARD} mt-8`}>
          <EmptyState icon={FiMessageSquare} title="Aucune unité sur votre compte">
            Les messages sont échangés par unité. Demandez à l’administrateur de préciser votre service.
          </EmptyState>
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-4xl px-[20px] pb-6 pt-8 sm:px-8 lg:px-10 lg:pt-10">
      <PageHeader eyebrow="Équipe" title="Messages" subtitle={`Salon de l’unité ${channel}`} />

      {channels.length > 1 && (
        <div className="mt-6">
          <Segmented
            value={channel}
            onChange={openChannel}
            allowEmpty={false}
            options={channels.map((name) => ({ value: name, label: unreadIn[name] ? `${name} (${unreadIn[name]})` : name }))}
          />
        </div>
      )}

      <section className={`${CARD} mt-6 flex h-[calc(100dvh-300px)] min-h-[440px] flex-col overflow-hidden`}>
        <div ref={list} onScroll={onScroll} className="min-h-0 flex-1 overflow-y-auto bg-mist/60 px-[14px] py-[16px] sm:px-6">
          {state.status === 'loading' ? (
            <SkeletonRows rows={5} />
          ) : state.status === 'error' ? (
            <EmptyState icon={FiAlertCircle} title="Messages indisponibles">
              Ils n’ont pas pu être chargés. Vérifiez la connexion, puis rechargez la page.
            </EmptyState>
          ) : messages.length === 0 ? (
            <EmptyState icon={FiMessageSquare} title="Aucun message">
              {access.canPost ? `Écrivez le premier message du salon ${channel}.` : `Les messages de l’unité ${channel} apparaîtront ici.`}
            </EmptyState>
          ) : (
            <>
              {hasOlder && (
                <div className="mb-[12px] text-center">
                  <button type="button" onClick={showOlder} className={`${BUTTON.ghost} ${SIZE.sm}`}>
                    Afficher les messages plus anciens
                  </button>
                </div>
              )}
              {days.map((day) => (
                <section key={day.label} className="mb-[12px]">
                  <p className="my-[12px] text-center text-[12px] font-semibold uppercase tracking-[0.06em] text-black/35">{day.label}</p>
                  <ul className="m-0 flex list-none flex-col gap-[10px] p-0">
                    {day.entries.map(({ message, showAuthor }) => (
                      <Bubble
                        key={message.id}
                        message={message}
                        own={message.userName === user.name}
                        showAuthor={showAuthor}
                        canDelete={access.canDelete(message)}
                        onReply={access.canPost ? setReplyTo : null}
                        onDelete={setDeleting}
                        onOpen={setViewing}
                      />
                    ))}
                  </ul>
                </section>
              ))}
            </>
          )}
        </div>

        {access.canPost ? (
          <div className="flex-none border-0 border-t-[1px] border-solid border-black/[0.06] bg-white px-[12px] py-[10px] sm:px-[16px]">
            {replyTo && (
              <div className="mb-2 flex items-center gap-2 rounded-xl bg-brand-50 px-[12px] py-2 text-[13px] text-brand-700">
                <FiCornerUpLeft size={14} aria-hidden="true" className="flex-none" />
                <span className="min-w-0 flex-1 truncate">
                  Réponse à <strong>{authorOf(replyTo)}</strong> : {replyTo.text || 'pièce jointe'}
                </span>
                <button type="button" onClick={() => setReplyTo(null)} aria-label="Annuler la réponse" className="flex h-7 w-7 flex-none cursor-pointer items-center justify-center rounded-full border-0 bg-[transparent] text-brand-700 hover:bg-brand-100">
                  <FiX size={14} aria-hidden="true" />
                </button>
              </div>
            )}
            {pending.length + uploaded.length > 0 && (
              <ul className="mb-2 flex list-none flex-wrap gap-1.5 p-0">
                {uploaded.map((item) => (
                  <li key={item.id} className="inline-flex max-w-full items-center gap-1 rounded-full bg-mist px-[10px] py-1 text-[12.5px] text-ink">
                    <FiCheck size={12} aria-hidden="true" className="flex-none text-green-600" />
                    <span className="truncate">{item.name}</span>
                    {!busy && (
                      <button
                        type="button"
                        onClick={() => {
                          setUploaded(uploaded.filter((other) => other.id !== item.id));
                          archiveDocuments([item]);
                        }}
                        aria-label={`Retirer ${item.name}`}
                        className="ml-0.5 flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border-0 bg-[transparent] text-black/45 hover:text-red-600"
                      >
                        <FiX size={12} aria-hidden="true" />
                      </button>
                    )}
                  </li>
                ))}
                {pending.map((file, index) => (
                  <li key={`${file.name}-${index}`} className="inline-flex max-w-full items-center gap-1 rounded-full bg-mist px-[10px] py-1 text-[12.5px] text-ink">
                    <FiPaperclip size={12} aria-hidden="true" className="flex-none" />
                    <span className="truncate">{file.name}</span>
                    {sending && sending.index === index && <span className="text-black/45">{Math.round(sending.ratio * 100)} %</span>}
                    {!busy && (
                      <button type="button" onClick={() => setPending(pending.filter((_, position) => position !== index))} aria-label={`Retirer ${file.name}`} className="ml-0.5 flex h-5 w-5 cursor-pointer items-center justify-center rounded-full border-0 bg-[transparent] text-black/45 hover:text-red-600">
                        <FiX size={12} aria-hidden="true" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {error && (
              <div className="mb-2">
                <Notice tone="red">{error}</Notice>
              </div>
            )}
            <div className="flex items-end gap-2">
              {documentsStatus.ready && (
                <>
                  <input
                    ref={picker}
                    type="file"
                    multiple
                    className="hidden"
                    aria-label="Joindre des fichiers"
                    onChange={(event) => {
                      const chosen = Array.from(event.target.files || []);
                      const tooLarge = chosen.filter((file) => documentsStatus.maxSize && file.size > documentsStatus.maxSize);
                      setError(tooLarge.length ? `Trop volumineux (${formatSize(documentsStatus.maxSize)} au plus) : ${tooLarge.map((file) => file.name).join(', ')}.` : '');
                      setPending((current) => [...current, ...chosen.filter((file) => !tooLarge.includes(file))]);
                      event.target.value = '';
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => picker.current && picker.current.click()}
                    disabled={busy}
                    aria-label="Joindre des fichiers"
                    title="Joindre des fichiers"
                    className={`${BUTTON.secondary} h-11 w-11 flex-none p-0`}
                  >
                    <FiPaperclip size={17} aria-hidden="true" />
                  </button>
                </>
              )}
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={onKeyDown}
                rows={Math.min(5, Math.max(1, text.split('\n').length))}
                placeholder={`Écrire au salon ${channel}…`}
                aria-label="Votre message"
                className="max-h-[140px] min-h-[44px] flex-1 resize-none rounded-2xl border-[1px] border-solid border-black/[0.1] bg-white px-[14px] py-[10px] text-[14.5px] leading-snug text-ink outline-none focus:border-brand-600/60 focus:ring-2 focus:ring-brand-600/20"
              />
              <button type="button" onClick={send} disabled={busy || (!text.trim() && !pending.length && !uploaded.length)} aria-label="Envoyer" title="Envoyer (Entrée)" className={`${BUTTON.primary} h-11 w-11 flex-none p-0`}>
                <FiSend size={17} aria-hidden="true" />
              </button>
            </div>
            <p className="mb-0 mt-1.5 hidden text-[11.5px] text-black/35 sm:block">Entrée pour envoyer · Maj + Entrée pour aller à la ligne</p>
          </div>
        ) : (
          <p className="m-0 flex-none border-0 border-t-[1px] border-solid border-black/[0.06] bg-white px-[16px] py-[14px] text-[13.5px] text-black/50">
            Vous pouvez lire les messages de ce salon ; l’écriture demande le niveau Modification.
          </p>
        )}
      </section>

      <Modal
        open={Boolean(deleting)}
        title="Supprimer le message"
        onClose={() => !busy && setDeleting(null)}
        footer={
          <>
            <button type="button" onClick={() => setDeleting(null)} className={`${BUTTON.secondary} ${SIZE.md}`}>
              Annuler
            </button>
            <button type="button" onClick={remove} disabled={busy} className={`${BUTTON.dangerSolid} ${SIZE.md}`}>
              {busy ? 'Suppression…' : 'Supprimer'}
            </button>
          </>
        }
      >
        <p className="m-0 text-[14.5px] leading-relaxed text-black/55">
          Le message sera supprimé pour tout le salon.
          {deleting && storedDocuments(deleting.documents).length > 0 && ' Ses pièces jointes restent conservées dans les archives du Dropbox du service.'}
        </p>
      </Modal>

      {viewing && <DocumentViewer item={viewing} onClose={() => setViewing(null)} canDownload={access.canDownload} />}
    </main>
  );
};

export default MessagesPage;
